// The contract every UI component talks to. Nothing above this layer may reference
// `window.chrome.webview` or `fetch` directly — that is the whole point of the abstraction.
//
// This app runs in two very different places:
//   • inside Revit, in a WebView2 panel, where commands are dispatched to C# and can touch the model;
//   • in an ordinary browser, as the public storefront, where only the catalog API exists.
//
// Choosing a web UI over WPF is only worth its costs if those two surfaces share one codebase.
// A transport interface is what makes that real instead of aspirational.

/** Intermediate progress for a long-running command (downloading a batch of families, ingesting an upload). */
export interface ProgressInfo {
  /** 0–1. */
  fraction: number;
  message?: string | null;
}

/**
 * Timeout for calls that block on a native modal dialog (file/folder pickers) rather than on Revit
 * work. Long enough that no realistic picking session trips it, short enough to still surface a
 * truly wedged channel eventually instead of hanging forever.
 */
export const PICKER_TIMEOUT_MS = 10 * 60_000;

export interface InvokeOptions {
  onProgress?: (progress: ProgressInfo) => void;
  signal?: AbortSignal;
  /**
   * Overrides the transport's default no-response timeout for this call. Commands that block on a
   * native modal (a file/folder picker) wait on the user, not Revit — the default timeout exists to
   * catch a wedged channel, not to bound how long someone takes to pick files. Those call sites pass
   * a long timeout here instead of inheriting the default.
   */
  timeoutMs?: number;
}

/** What the current environment can actually do. Drives feature gating in the UI. */
export interface TransportCaps {
  /** True only when a live Revit session is on the other end. */
  revit: boolean;
  /** Revit release year, e.g. 2025. Undefined in the browser — there is no host to ask. */
  revitYear?: number;
  /** "metric" | "imperial", from the active document. Undefined in the browser. */
  unitSystem?: string;
}

export interface Transport {
  readonly kind: "revit" | "http";
  readonly caps: TransportCaps;

  /** Resolves once the transport knows its capabilities. Safe to call more than once. */
  init(): Promise<void>;

  invoke<T = unknown>(command: string, payload?: unknown, options?: InvokeOptions): Promise<T>;
}

/**
 * Thrown when a command exists in the app but not in this environment — asking the browser to
 * load a family into a Revit project, for example.
 *
 * A distinct type rather than a generic Error so the UI can render "open this in Revit to
 * continue" instead of a failure toast. Components should still gate on `caps.revit` first;
 * this is the backstop for the cases they miss.
 */
export class CommandUnavailableError extends Error {
  constructor(
    public readonly command: string,
    public readonly transportKind: Transport["kind"],
  ) {
    super(
      transportKind === "http"
        ? `"${command}" needs a running Revit session; it is not available in the browser.`
        : `"${command}" is not a known command.`,
    );
    this.name = "CommandUnavailableError";
  }
}

/** A command failed on the other side. Carries the host/server message verbatim. */
export class TransportError extends Error {
  constructor(
    public readonly command: string,
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "TransportError";
  }
}
